import React, { createContext, useContext, useEffect } from 'react';
import './SideModal.scss';

type SideModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'expanded' | 'fullscreen';

const SideModalContext = createContext<SideModalSize>('md');

interface SideModalProps {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: SideModalSize;
  headerActions?: React.ReactNode;
  hideBackdrop?: boolean;
}

const SideModal: React.FC<SideModalProps> = ({
  isOpen,
  toggle,
  title,
  children,
  footer,
  size = 'md',
  headerActions,
  hideBackdrop = false,
}) => {
  const parentSize = useContext(SideModalContext);
  // Parent genişletilmişse child da genişletilmiş açılsın
  const effectiveSize = parentSize === 'expanded' && size !== 'expanded' ? 'expanded' : size;
  // ESC tuşu ile kapatma
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        toggle();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, toggle]);

  // Body scroll'u engelle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <SideModalContext.Provider value={effectiveSize}>
      {/* Backdrop - only show for first level modal */}
      {!hideBackdrop && <div className="side-modal-backdrop" onClick={toggle} />}

      {/* Modal Panel */}
      <div className={`side-modal-panel side-modal-${effectiveSize}`}>
        {/* Header */}
        <div className="side-modal-header">
          <h5 className="side-modal-title">{title}</h5>
          <div className="side-modal-header-actions">
            {headerActions}
            <button
              type="button"
              className="side-modal-close"
              onClick={toggle}
              aria-label="Kapat"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="side-modal-body">{children}</div>

        {/* Footer */}
        {footer && <div className="side-modal-footer">{footer}</div>}
      </div>
    </SideModalContext.Provider>
  );
};

export default SideModal;
